import { Component, ViewEncapsulation, OnInit, ViewChild, ElementRef } from '@angular/core';
import { RouteConfigLoadEnd, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import { ColumnMode, DatatableComponent, TableColumn } from '@swimlane/ngx-datatable';
import { FormGroup, Validators, FormBuilder, FormControl } from '@angular/forms';
import { Md5 } from 'ts-md5';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { EmployeeUsersService } from 'src/app/services/employee-users/employee-users.service';
import { RingcentralService } from 'src/app/services/ringcentral/ringcentral.service';
import { UserWizardComponent } from '../../shared/wizards/user-wizard/user-wizard.component';
import { UserService } from 'src/app/services/user/user.service';
import { STATES, TIME_ZONES } from 'src/app/shared/constants';
import { EditClientComponent } from '../client-users/edit-client/edit-client.component';
import { CampaignService } from 'src/app/services/campaigns/campaign.service';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
	selector: 'app-users',
	templateUrl: './users.component.html',
	styleUrls: ['./users.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class UsersComponent implements OnInit {
	loading = false;
	columnMode = ColumnMode;
	reorderable: boolean;
	msgError: string = '';

	allUsers: any[] = [];
	allUsersRaw: any[] = [];

	filterStatus = 1;
	filterPortal: string = 'All';
	currentStatus: number = 1;
	searchText: string = '';

	showPass: any = -1;
	editUserMode: boolean = false;
	createEmployeeUserForm: FormGroup;

	permissionsList: any[] = [];
	activePermissions: any[] = [];

	wizardStep: number = 1;
	selectedUserType: 'customer' | 'admin' | 'caller' | null = null;
	typeNotSelected: boolean = false;
	submittingUser: boolean = false;
	employeeUsersForSelect: any[] = [];

	basicInfoForm: FormGroup;
	employeeExtraForm: FormGroup;
	contactInfoForm: FormGroup;
	locationForm: FormGroup;
	lmsInfoForm: FormGroup;

	timezones: any[] = [];
	states: any[] = [];

	userInfo: FormGroup = new FormGroup({
		first_name: new FormControl(''),
		last_name: new FormControl(''),
		alias: new FormControl(''),
		primary_email: new FormControl(''),
		email2: new FormControl(''),
		email3: new FormControl(''),
		mobile_phone: new FormControl(''),
		phone_home: new FormControl(''),
		phone_work: new FormControl(''),
		phone_other: new FormControl(''),
		company_name: new FormControl(''),
		account_associated: new FormControl(''),
		address: new FormControl(''),
		city: new FormControl(''),
		state: new FormControl(''),
		zip_code: new FormControl(''),
		plan: new FormControl(''),
		isMain: new FormControl(false),
		isPremium: new FormControl(false),
		startupFeeAmount: new FormControl(''),
	});

	sendPasswordForm: FormGroup;

	linkCampaign = false;
	campaigns: any[] = [];

	activeUsers = 0;
	inactiveUsers = 0;

	onlineUsersCount = 0;

	totalOpenQcReviews = 0;

	@ViewChild(DatatableComponent) table: DatatableComponent;

	avatarColors = ['#1abc9c', '#3498db', '#9b59b6', '#e67e22', '#e74c3c', '#2ecc71', '#f39c12', '#16a085'];
	clientCount = 0;

	currentUserRole: string = '';

	constructor(
		private modalService: NgbModal,
		private employeeUsersService: EmployeeUsersService,
		public ringcentralSrv: RingcentralService,
		private userSrv: UserService,
		private router: Router,
		public utils: UtilsService,
		private fb: FormBuilder,
		private authSrv: AuthenticationService,
		private campaignSrv: CampaignService,
		private dashboardSrv: DashboardService,
	) {
		this.createEmployeeUserForm = this.fb.group({
			name: ['', Validators.required],
			lastname: ['', Validators.required],
			ringcentral_ext: ['', Validators.required],
			email: ['', [Validators.required, Validators.email]],
			isAdmin: [false],
			isCaller: [true],
			isDbReset: [false],
			id: [null],
		});

		this.sendPasswordForm = this.fb.group({
			email: ['', [Validators.required, Validators.email]],
		});

		this.initWizardForms();
	}

	ngOnInit() {
		this.dashboardSrv.onlineUsersCount$.subscribe((count) => {
			this.onlineUsersCount = count;
		});

		const fbUser = this.authSrv.currentUser();
		if (fbUser?.email) {
			this.authSrv.getLmsUserData(fbUser.email).subscribe((data: any) => {
				this.currentUserRole = data?.body?.role || '';
			});
		}
		this.loadAllUsers(1);
		this.timezones = TIME_ZONES;
		this.states = STATES;
	}

	loadAllUsers(status: number) {
		this.loading = true;
		this.currentStatus = status;

		this.userSrv.getAllUsers(status).subscribe((data: any) => {
			const users = data.body || [];
			this.allUsersRaw = users.map((u: any) => ({
				...u,
				portalType: u.role === 'CUS' ? 'Customer' : 'Employee',
			}));

			this.clientCount = this.allUsersRaw.filter((u) => u.role === 'CUS').length;
			this.totalOpenQcReviews = this.allUsersRaw.reduce((total, row) => total + Number(row.open_qc_reviews || 0), 0);
			this.applyFilters();
			this.loading = false;
		});
	}

	filterByStatus(status: number) {
		this.currentStatus = status;
		// this.filterStatus = status === 1 ? 'Active' : 'Inactive';
		this.loadAllUsers(status);
	}

	filterByPortal(portal: string) {
		this.filterPortal = portal;
		this.applyFilters();
	}

	updateFilterUnified(event: any) {
		this.searchText = event.target.value.toLowerCase();
		this.applyFilters();
	}

	applyFilters() {
		let result = [...this.allUsersRaw];

		if (this.filterPortal !== 'All') {
			const roleMap: { [key: string]: string[] } = {
				Admin: ['ADM', 'SADM'],
				Caller: ['CAL'],
				Customer: ['CUS'],
			};
			const roleValue = roleMap[this.filterPortal];
			result = result.filter((u) => roleValue.includes(u.role));
		}

		if (this.searchText) {
			const val = this.searchText;
			result = result.filter((u) => (u.name && u.name.toLowerCase().includes(val)) || (u.login && u.login.toLowerCase().includes(val)) || (u.alias && u.alias.toLowerCase().includes(val)) || (u.email1 && u.email1.toLowerCase().includes(val)) || (u.company_name && u.company_name.toLowerCase().includes(val)));
		}

		this.allUsers = result;
	}

	deactivateUserConfirm(user: any) {
		Swal.fire({
			title: 'Deactivate ' + user.name + ' ?',
			text: 'ARE YOU SURE?',
			showCancelButton: true,
			confirmButtonText: 'Yes',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (result.value) {
				this.employeeUsersService.deactiveUser(user.id).subscribe((data: any) => {
					Swal.fire('User Deactivated');
					this.loadAllUsers(this.currentStatus);
				});
			}
		});
	}

	activateUserConfirm(user: any) {
		Swal.fire({
			title: 'Activate ' + user.name + ' ?',
			text: 'ARE YOU SURE?',
			showCancelButton: true,
			confirmButtonText: 'Yes',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (result.value) {
				this.employeeUsersService.activateUser(user.id).subscribe((data: any) => {
					Swal.fire('User Activated');
					this.loadAllUsers(this.currentStatus);
				});
			}
		});
	}

	deactivateUserAdminConfirm(user: any) {
		Swal.fire({
			title: 'Deactivate Admin Role for ' + user.name + '?',
			text: 'ARE YOU SURE?',
			showCancelButton: true,
			confirmButtonText: 'Yes',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (result.value) {
				this.employeeUsersService.deactivateUserAdmin(user.id).subscribe((data: any) => {
					Swal.fire('Admin Role Deactivated');
					this.loadAllUsers(this.currentStatus);
				});
			}
		});
	}

	activateUserAdminConfirm(user: any) {
		Swal.fire({
			title: 'Activate Admin Role for ' + user.name + '?',
			text: 'ARE YOU SURE?',
			showCancelButton: true,
			confirmButtonText: 'Yes',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (result.value) {
				this.employeeUsersService.activateUserAdmin(user.id).subscribe((data: any) => {
					Swal.fire('Admin Role Activated');
					this.loadAllUsers(this.currentStatus);
				});
			}
		});
	}

	getUserPass(fullname: any) {
		const last = fullname.split(' ').pop();
		return this.utils.getUserPass(last);
	}

	openEditUserModal(content, user) {
		this.editUserMode = true;
		this.createEmployeeUserForm.reset();
		const nameParts = user.name.split(' ');
		this.createEmployeeUserForm.patchValue({
			name: nameParts[0],
			lastname: nameParts[1] || '',
			ringcentral_ext: user.ringcentral_ext,
			email: user.email1,
			isAdmin: user.isAdmin,
			isCaller: user.isCaller,
			isDbReset: user.isDbReset,
			id: user.id,
		});
		this.activePermissions = user.permissions ? [...user.permissions] : [];
		this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title', centered: true }).result.then(
			(result) => {
				console.info(result);
			},
			(reason) => {
				console.info(reason);
			},
		);
	}

	validateAndSubmit() {
		this.createEmployeeUserForm.markAllAsTouched();
		if (this.createEmployeeUserForm.valid) {
			const formData = this.createEmployeeUserForm.value;
			const md5 = new Md5();
			formData.pass = md5.appendStr(this.utils.getUserPass(formData.lastname)).end();

			this.userSrv.createEmployeeUser(formData).subscribe(
				(data) => {
					this.createEmployeeUserForm.reset();
					this.modalService.dismissAll();
					this.loadAllUsers(this.currentStatus);
					Swal.fire({ title: 'Success', icon: 'success', text: 'Employee user created successfully', confirmButtonText: 'Ok' });
				},
				(error) => {
					console.error(error);
					Swal.fire({ title: 'Error', icon: 'error', text: 'Something went wrong', confirmButtonText: 'Ok' });
				},
			);
		}
	}

	updateEmployeeUser() {
		this.createEmployeeUserForm.markAllAsTouched();
		if (this.createEmployeeUserForm.valid) {
			const formData = this.createEmployeeUserForm.getRawValue();

			formData.isAdmin = formData.isAdmin ? 1 : 0;
			formData.isCaller = formData.isCaller ? 1 : 0;
			formData.isDbReset = formData.isDbReset ? 1 : 0;

			this.userSrv.updateEmployeeUser(formData).subscribe(
				(data) => {
					this.createEmployeeUserForm.reset();
					this.modalService.dismissAll();
					this.loadAllUsers(this.currentStatus);
					Swal.fire({ title: 'Success', icon: 'success', text: 'Employee user updated successfully', confirmButtonText: 'Ok' });
				},
				(error) => {
					console.error(error);
					Swal.fire({ title: 'Error', icon: 'error', text: 'Something went wrong', confirmButtonText: 'Ok' });
				},
			);
		}
	}

	sendPasswordEmployeeCreation(email, name) {
		Swal.fire({
			title: 'Sending...',
			text: 'Sending password creation email',
			confirmButtonText: 'Ok',
			didOpen: () => {
				Swal.showLoading();
				this.userSrv.send_password_employee_creation(email, name).subscribe(
					(data: any) => {
						Swal.close();
						this.modalService.dismissAll();
						Swal.fire({ title: 'Success', icon: 'success', text: 'Email sent successfully', confirmButtonText: 'Ok' });
					},
					(error) => {
						Swal.close();
						Swal.fire({ title: 'Error', icon: 'error', text: 'Failed to send email', confirmButtonText: 'Ok' });
					},
				);
			},
		});
	}

	isPermissionActive(permId: any): boolean {
		return this.activePermissions.includes(permId);
	}

	togglePermission(permId: any, event: any) {
		if (event.target.checked) {
			this.activePermissions.push(permId);
		} else {
			this.activePermissions = this.activePermissions.filter((id) => id !== permId);
		}
	}

	openEditUser(user_id: number, role: string) {
		const modalRef = this.modalService.open(EditClientComponent, {
			size: 'xl',
			centered: true,
			scrollable: true,
			backdrop: 'static',
			windowClass: 'fixed-size-modal',
		});
		modalRef.componentInstance.user_id = user_id;
		modalRef.componentInstance.userRole = role;
		modalRef.result
			.then(
				(result) => {
					if (result === 'updated') {
						this.loadAllUsers(this.currentStatus);
					}
				},
				(reason) => {},
			)
			.catch(() => {});
	}

	deactivateClientUserConfirm(user: any) {
		Swal.fire({
			title: 'Deactivate ' + user.first + ' ' + user.last + ' ?',
			text: 'ARE YOU SURE?',
			showCancelButton: true,
			confirmButtonText: 'Yes',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (result.value) {
				this.loading = true;
				this.userSrv.deactivateUser(user.id).subscribe((data: any) => {
					Swal.fire('This user is not active anymore');
					this.loadAllUsers(this.currentStatus);
				});
			}
		});
	}

	activateClientUserConfirm(user: any) {
		Swal.fire({
			title: 'Activate ' + user.first + ' ' + user.last + ' ?',
			text: 'ARE YOU SURE?',
			showCancelButton: true,
			confirmButtonText: 'Yes',
			cancelButtonText: 'Cancel',
		}).then((result) => {
			if (result.value) {
				this.loading = true;
				this.userSrv.activateUser(user.id).subscribe((data: any) => {
					Swal.fire('Correctly activated user');
					this.loadAllUsers(this.currentStatus);
				});
			}
		});
	}

	resetPassword(row?: any) {
		const email = row ? row.email1 : this.userInfo.get('primary_email')?.value;

		if (!email) {
			Swal.fire({
				title: 'Error',
				icon: 'error',
				text: 'No email found for this user',
			});
			return;
		}

		Swal.fire({
			title: 'Sending',
			text: 'Sending email reset password',
			confirmButtonText: 'Ok',
			didOpen: () => {
				Swal.showLoading();
				this.authSrv.resetPassword(email).then(
					(data: any) => {
						Swal.close();
						Swal.fire({
							title: 'Success',
							text: 'Email sent correctly',
							showCancelButton: false,
							confirmButtonText: 'Ok',
							cancelButtonText: 'Cancel',
						});
					},
					(error) => {
						Swal.fire({
							title: 'Error',
							icon: 'error',
							text: error,
							showCancelButton: false,
							confirmButtonText: 'Ok',
							cancelButtonText: 'Cancel',
						});
					},
				);
			},
		});
	}

	testEmail(row?: any) {
		const email = row ? row.email1 : this.userInfo.get('primary_email')?.value;
		const name = row ? row.name : this.userInfo.get('first_name')?.value + ' ' + this.userInfo.get('last_name')?.value;

		Swal.fire({
			title: 'Sending',
			text: 'Sending test email',
			confirmButtonText: 'Ok',
			didOpen: () => {
				Swal.showLoading();
				this.userSrv.testUserEmail(email, name).subscribe(
					(data: any) => {
						Swal.close();
						Swal.fire({
							title: 'Success',
							text: 'Email sent correctly',
							showCancelButton: false,
							confirmButtonText: 'Ok',
							cancelButtonText: 'Cancel',
						});
					},
					(error) => {
						Swal.fire({
							title: 'Error',
							icon: 'error',
							text: error,
							showCancelButton: false,
							confirmButtonText: 'Ok',
							cancelButtonText: 'Cancel',
						});
					},
				);
			},
		});
	}

	sendLinkToCustomer(row?: any) {
		const email1 = row ? row.email1 : this.userInfo.get('primary_email')?.value;

		Swal.fire({
			title: 'Sending',
			text: 'Sending email reset password',
			confirmButtonText: 'Ok',
			didOpen: () => {
				Swal.showLoading();
				this.userSrv.sendLinkCreationCustomer(`https://app.lmsleads.com/#/auth/signup/cover/${email1}`, email1).subscribe(
					(data: any) => {
						Swal.close();
						Swal.fire({
							title: 'Success',
							text: 'Link is sended correctly',
							showCancelButton: false,
							confirmButtonText: 'Ok',
							cancelButtonText: 'Cancel',
						});
					},
					(error) => {
						Swal.fire({
							title: 'Error',
							icon: 'error',
							text: error,
							showCancelButton: false,
							confirmButtonText: 'Ok',
							cancelButtonText: 'Cancel',
						});
					},
				);
			},
		});
	}

	initWizardForms() {
		this.basicInfoForm = this.fb.group({
			name: ['', Validators.required],
			lastname: ['', Validators.required],
			email: ['', [Validators.required, Validators.email]],
		});

		this.employeeExtraForm = this.fb.group({
			ringcentral_ext: ['', Validators.required],
			isAdmin: [false],
			isCaller: [true],
			isDbReset: [false],
			isSuperAdmin: [false],
		});

		this.contactInfoForm = this.fb.group({
			company_name: ['', Validators.required],
			email1: [''],
			mobile_phone: [''],
			email2: [''],
			email3: [''],
			phone_home: [''],
			phone_work: [''],
			phone_other: [''],
		});

		this.locationForm = this.fb.group({
			address: [''],
			timezone: [''],
			city: [''],
			state: [''],
			zip_code: [''],
		});

		this.lmsInfoForm = this.fb.group({
			account_associated: ['', Validators.required],
			user_type: ['1', Validators.required],
			alias: [''],
			source: ['T3direct Marketing'],
		});
	}

	loadEmployeesForSelect() {
		this.userSrv.getActiveEmployees().subscribe((data: any) => {
			this.employeeUsersForSelect = data.body || [];
		});
	}

	openAddUserModal(content: any) {
		this.wizardStep = 1;
		this.selectedUserType = null;
		this.typeNotSelected = false;
		this.submittingUser = false;
		this.basicInfoForm.reset();
		this.employeeExtraForm.reset();
		this.employeeExtraForm.patchValue({ isCaller: true });
		this.contactInfoForm.reset();
		this.locationForm.reset();
		this.lmsInfoForm.reset();
		this.lmsInfoForm.patchValue({ user_type: '1', source: 'T3direct Marketing' });

		this.loadEmployeesForSelect();
		this.getCampaignsForSelect();

		this.modalService.open(content, {
			ariaLabelledBy: 'modal-basic-title',
			centered: true,
			size: 'lg',
			backdrop: 'static',
			windowClass: 'fixed-wizard-modal',
		});
	}

	selectUserType(type: 'customer' | 'admin' | 'caller') {
		this.selectedUserType = type;
		this.typeNotSelected = false;

		if (type === 'admin') {
			this.employeeExtraForm.patchValue({ isAdmin: true, isCaller: false, isDbReset: true });
			this.employeeExtraForm.get('isDbReset').disable();
		} else if (type === 'caller') {
			this.employeeExtraForm.patchValue({ isAdmin: false, isCaller: true, isDbReset: false, isSuperAdmin: false });
			this.employeeExtraForm.get('isDbReset').enable();
		}
	}

	isLastStep(): boolean {
		if (this.selectedUserType === 'customer') return this.wizardStep === 4;
		return this.wizardStep === 2; // admin / caller only have 2 steps
	}

	wizardNext() {
		if (this.wizardStep === 1) {
			this.basicInfoForm.markAllAsTouched();
			if (!this.selectedUserType) {
				this.typeNotSelected = true;
				return;
			}
			if (this.basicInfoForm.invalid) return;
		}

		if (this.wizardStep === 2 && this.selectedUserType !== 'customer') {
			this.employeeExtraForm.markAllAsTouched();
			if (this.employeeExtraForm.invalid) return;
		}

		if (this.wizardStep === 2 && this.selectedUserType === 'customer') {
			this.contactInfoForm.markAllAsTouched();
			if (this.contactInfoForm.invalid) return;
		}

		if (this.wizardStep === 3) {
		}

		this.wizardStep++;
	}

	wizardBack() {
		if (this.wizardStep > 1) this.wizardStep--;
	}

	submitNewUser() {
		this.submittingUser = true;

		if (this.selectedUserType === 'customer') {
			this.lmsInfoForm.markAllAsTouched();
			if (this.lmsInfoForm.invalid) {
				this.submittingUser = false;
				return;
			}
			this._createCustomerUser();
		} else {
			this.employeeExtraForm.markAllAsTouched();
			if (this.employeeExtraForm.invalid) {
				this.submittingUser = false;
				return;
			}
			this._createEmployeeUser();
		}
	}

	private _createEmployeeUser() {
		const md5 = new Md5();
		const basic = this.basicInfoForm.value;
		const extra = this.employeeExtraForm.value;
		const fbUser = this.authSrv.currentUser();

		let role = 'CAL';
		if (this.selectedUserType === 'admin') {
			role = this.isSuperAdmin && extra.isSuperAdmin ? 'SADM' : 'ADM';
		}

		const payload = {
			name: basic.name,
			lastname: basic.lastname,
			email: basic.email,
			ringcentral_ext: extra.ringcentral_ext || '',
			isAdmin: extra.isAdmin,
			isCaller: extra.isCaller,
			isDbReset: this.selectedUserType === 'admin' ? true : extra.isDbReset,
			role: role,
			creator_email: fbUser?.email || '',
			pass: md5.appendStr(this.utils.getUserPass(basic.lastname)).end(),
		};

		this.userSrv.createEmployeeUser(payload).subscribe((data: any) => {
			this.submittingUser = false;
			if (data.success && !data.body?.error) {
				this.modalService.dismissAll();
				this.loadAllUsers(this.currentStatus);
				Swal.fire({ title: 'Success', icon: 'success', text: 'Customer user created successfully', confirmButtonText: 'Ok' });
			} else {
				const errorMsg = data.body?.error || 'Please review the information';
				const isDuplicate = errorMsg.includes('Duplicate entry') && errorMsg.includes('unique_email');
				Swal.fire({
					title: 'Error',
					icon: 'error',
					text: isDuplicate ? 'This email is already registered. Please use a different email.' : errorMsg,
					confirmButtonText: 'Ok',
				});
			}
		});
	}

	private _createCustomerUser() {
		const md5 = new Md5();
		const basic = this.basicInfoForm.value;
		const contact = this.contactInfoForm.value;
		const location = this.locationForm.value;
		const lms = this.lmsInfoForm.value;

		const payload = {
			first_name: basic.name,
			last_name: basic.lastname,
			email1: basic.email,
			company_name: contact.company_name || '',
			mobile_phone: contact.mobile_phone || '',
			email2: contact.email2 || '',
			email3: contact.email3 || '',
			phone_home: contact.phone_home || '',
			phone_work: contact.phone_work || '',
			phone_other: contact.phone_other || '',
			address: location.address || '',
			city: location.city || '',
			state: location.state || '',
			zip_code: location.zip_code || '',
			timezone: location.timezone || '',
			account_associated: lms.account_associated,
			user_type: lms.user_type,
			alias: lms.alias || '',
		};

		this.userSrv.createSingleUser(payload).subscribe(
			(data: any) => {
				this.submittingUser = false;
				if (data.success && !data.body?.error) {
					this.modalService.dismissAll();
					this.loadAllUsers(this.currentStatus);
					Swal.fire({ title: 'Success', icon: 'success', text: 'Customer user created successfully', confirmButtonText: 'Ok' });
				} else {
					const errorMsg = data.body?.error || 'Please review the information';
					const isDuplicate = errorMsg.includes('Duplicate entry') && errorMsg.includes('unique_email');
					Swal.fire({
						title: 'Error',
						icon: 'error',
						text: isDuplicate ? 'This email is already registered. Please use a different email.' : errorMsg,
						confirmButtonText: 'Ok',
					});
				}
			},
			(error) => {
				console.error(error);
				this.submittingUser = false;
				Swal.fire({ title: 'Error', icon: 'error', text: 'Something went wrong', confirmButtonText: 'Ok' });
			},
		);
	}

	linkCampaignToggle() {
		this.linkCampaign = !this.linkCampaign;
		if (!this.linkCampaign) {
			this.userInfo.get('alias')?.setValue('');
			this.lmsInfoForm.patchValue({ alias: '' });
			this.lmsInfoForm.get('alias')?.disable();
		} else {
			this.lmsInfoForm.get('alias')?.enable();
			this.lmsInfoForm.get('alias')?.markAsUntouched();
		}
	}

	onCampaignSelect(event: any) {
		const selectedValue = event.target.value;
		this.lmsInfoForm.patchValue({ alias: selectedValue });
	}

	getCampaignsForSelect() {
		this.campaignSrv.getCampaignsForSelect().subscribe((data: any) => {
			this.campaigns = data.body;
		});
	}

	onActivate(event: any): void {
		if (event.type === 'dblclick') {
			this.openEditUser(event.row.id, event.row.role);
		}
	}

	getInitials(name: string): string {
		if (!name) return '';

		const parts = name.trim().split(' ');
		const first = parts[0]?.charAt(0) || '';
		const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';

		return (first + last).toUpperCase();
	}

	getAvatarColor(name: string): string {
		let hash = 0;

		for (let i = 0; i < name.length; i++) {
			hash = name.charCodeAt(i) + ((hash << 5) - hash);
		}

		const index = Math.abs(hash % this.avatarColors.length);

		return this.avatarColors[index];
	}

	openMail(email: string) {
		window.location.href = `mailto:${email}`;
	}

	get isSuperAdmin(): boolean {
		return this.currentUserRole === 'SADM';
	}
}
